/* eslint-disable react-refresh/only-export-components -- the preset resolver is exported so a
   screen can compute its own default range with exactly the arithmetic the chips use. Splitting it
   out is how the chip labelled "Last month" and the screen's initial state stop agreeing. */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/**
 * The period control every report shares.
 *
 * 🔴 EVERY DATE HERE IS A LOCAL CALENDAR DATE, BUILT FROM `getFullYear/getMonth/getDate`. Never
 * `toISOString()`: it converts to UTC first, so a user east of Greenwich asking for "this month" on
 * the 1st gets last month, and a user west of it gets next month on the last day. The values are
 * plain `yyyy-MM` (month mode) or `yyyy-MM-dd` (day mode) strings, which also compare correctly as
 * strings, which is what the invalid-range check relies on.
 */

export type PeriodMode = 'month' | 'day';

export interface PeriodValue {
  /** `yyyy-MM` in month mode, `yyyy-MM-dd` in day mode. */
  from: string;
  /** Inclusive. Same shape as `from`. */
  to: string;
}

export type PeriodPresetKey =
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'last3Months'
  | 'last30Days'
  | 'thisYear'
  | 'custom';

const MONTH_PRESETS: PeriodPresetKey[] = [
  'thisMonth',
  'lastMonth',
  'last3Months',
  'thisYear',
  'custom',
];

const DAY_PRESETS: PeriodPresetKey[] = [
  'today',
  'thisWeek',
  'thisMonth',
  'lastMonth',
  'last30Days',
  'custom',
];

const pad = (n: number) => String(n).padStart(2, '0');

export const monthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/**
 * "This week" starts on Monday. The region runs several different working weeks (Sun-Thu in KSA,
 * Mon-Fri elsewhere) and a per-tenant week start is not modelled anywhere the client can read, so
 * the control commits to the ISO week rather than guessing per tenant.
 */
const startOfWeek = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));

/** The range a preset resolves to today. `custom` has no range and returns null. */
export function resolvePeriodPreset(
  preset: PeriodPresetKey,
  mode: PeriodMode,
  today: Date = new Date(),
): PeriodValue | null {
  if (preset === 'custom') return null;
  const key = mode === 'month' ? monthKey : dayKey;

  switch (preset) {
    case 'today':
      return { from: key(today), to: key(today) };
    case 'thisWeek': {
      const start = startOfWeek(today);
      return { from: key(start), to: key(addDays(start, 6)) };
    }
    case 'thisMonth':
      return mode === 'month'
        ? { from: monthKey(today), to: monthKey(today) }
        : { from: dayKey(startOfMonth(today)), to: dayKey(endOfMonth(today)) };
    case 'lastMonth': {
      const previous = addMonths(today, -1);
      return mode === 'month'
        ? { from: monthKey(previous), to: monthKey(previous) }
        : { from: dayKey(startOfMonth(previous)), to: dayKey(endOfMonth(previous)) };
    }
    case 'last3Months':
      return { from: key(addMonths(today, -2)), to: key(today) };
    case 'last30Days':
      return { from: dayKey(addDays(today, -29)), to: dayKey(today) };
    case 'thisYear':
      return mode === 'month'
        ? { from: `${today.getFullYear()}-01`, to: monthKey(today) }
        : { from: dayKey(new Date(today.getFullYear(), 0, 1)), to: dayKey(today) };
    default:
      return null;
  }
}

export interface PeriodPickerProps {
  mode: PeriodMode;
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  /** Overrides the preset set for the mode. `custom` is always appended if omitted. */
  presets?: PeriodPresetKey[];
  disabled?: boolean;
  className?: string;
  testId?: string;
}

const CHIP_BASE =
  'rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50';
const CHIP_ACTIVE = 'border-primary bg-primary/10 text-primary';
const CHIP_IDLE =
  'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground';

const INPUT_CLASS =
  'rounded-md border border-input bg-background px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 aria-[invalid=true]:border-destructive';

export function PeriodPicker({
  mode,
  value,
  onChange,
  presets,
  disabled = false,
  className,
  testId,
}: PeriodPickerProps) {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = useState(false);

  const presetKeys = useMemo(() => {
    const base = presets ?? (mode === 'month' ? MONTH_PRESETS : DAY_PRESETS);
    return base.includes('custom') ? base : [...base, 'custom' as PeriodPresetKey];
  }, [presets, mode]);

  const activePreset = useMemo(() => {
    const now = new Date();
    return presetKeys.find((key) => {
      const range = resolvePeriodPreset(key, mode, now);
      return !!range && range.from === value.from && range.to === value.to;
    });
  }, [presetKeys, mode, value.from, value.to]);

  const invalid = !!value.from && !!value.to && value.from > value.to;
  const showCustom = customOpen || activePreset === undefined;

  const PRESET_LABEL: Record<PeriodPresetKey, string> = {
    today: t('reports.preset.today'),
    thisWeek: t('reports.preset.thisWeek'),
    thisMonth: t('reports.preset.thisMonth'),
    lastMonth: t('reports.preset.lastMonth'),
    last3Months: t('reports.preset.last3Months'),
    last30Days: t('reports.preset.last30Days'),
    thisYear: t('reports.preset.thisYear'),
    custom: t('reports.preset.custom'),
  };

  const select = (key: PeriodPresetKey) => {
    if (key === 'custom') {
      setCustomOpen(true);
      return;
    }
    const range = resolvePeriodPreset(key, mode);
    if (range) {
      setCustomOpen(false);
      onChange(range);
    }
  };

  const inputType = mode === 'month' ? 'month' : 'date';

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid={testId}>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('reports.period')}>
        {presetKeys.map((key) => {
          const active = key === 'custom' ? showCustom && !activePreset : activePreset === key;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              onClick={() => select(key)}
              data-testid={`period-preset-${key}`}
              className={cn(CHIP_BASE, active ? CHIP_ACTIVE : CHIP_IDLE)}
            >
              {PRESET_LABEL[key]}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type={inputType}
            value={value.from}
            max={value.to || undefined}
            disabled={disabled}
            aria-invalid={invalid}
            aria-label={t('reports.from')}
            data-testid="period-from"
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            className={INPUT_CLASS}
          />
          <span className="text-sm text-muted-foreground">{t('reports.to')}</span>
          <input
            type={inputType}
            value={value.to}
            min={value.from || undefined}
            disabled={disabled}
            aria-invalid={invalid}
            aria-label={t('reports.to')}
            data-testid="period-to"
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            className={INPUT_CLASS}
          />
        </div>
      )}

      {invalid && (
        <p className="text-xs text-destructive-strong" role="alert" data-testid="period-invalid">
          {t('reports.invalidRange')}
        </p>
      )}
    </div>
  );
}

export default PeriodPicker;
